import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { SidebarNav } from './components/ui/SidebarNav';
import { AnalysisStatusBar } from './components/ui/AnalysisStatusBar';
import { NotFoundPage } from './components/ui/NotFoundPage';
import { SupportModeBanner } from './components/support/SupportModeBanner';
import { HomeRoute } from './components/agent/HomeRoute';
import { CatalogRedirect } from './components/catalog/CatalogRedirect';
import { SectionRedirect } from './components/catalog/SectionRedirect';
import { CatalogLayout } from './components/catalog/CatalogLayout';
import { AnalyticsTab } from './components/analytics/AnalyticsTab';
import { ConnectionsTab } from './components/connections/ConnectionsTab';
import { ConnectionDetail } from './components/connections/ConnectionDetail';
import { TeamTab } from './components/team/TeamTab';
import { SettingsTab } from './components/settings/SettingsTab';
import { darkTheme, FONT } from './theme/tokens';

const t = darkTheme;

// Catalog workspace sections (SC-2457) — lazy so the table grid + its facet /
// drawer / dock machinery only ship when a catalog section is actually opened.
// They render inside CatalogLayout's <Suspense> boundary.
const TableTab = lazy(() => import('./components/catalog/TableTab'));
const AttributesRoute = lazy(() => import('./components/catalog/AttributesRoute'));
const ProfileRoute = lazy(() => import('./components/catalog/ProfileRoute'));
const ChangesRoute = lazy(() => import('./components/catalog/ChangesRoute'));
const FindingsRoute = lazy(() => import('./components/catalog/FindingsRoute'));
const BoardRoute = lazy(() => import('./components/boards/BoardRoute'));

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: 40, color: '#EF4444' }}>Something went wrong. Please refresh.</div>
      }
    >
      <div
        style={{
          fontFamily: FONT,
          background: t.bg,
          color: t.text,
          fontSize: 13,
          lineHeight: 1.5,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Staff support mode (SC-2948) — above everything, never scrolled away. */}
        <SupportModeBanner />
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <SidebarNav />
          <main style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
            <Routes>
              <Route index element={<HomeRoute />} />
              {/* Legacy entry points → the canonical catalog workspace URL. The
                  sidebar entities item navigates to /entities; CatalogRedirect
                  forwards it (and the retired /table bookmark) to
                  /catalogs/:id/entities once a catalog resolves. */}
              <Route path="entities" element={<CatalogRedirect />} />
              <Route path="table" element={<CatalogRedirect />} />
              <Route path="catalog" element={<CatalogRedirect />} />
              {/* Catalog workspace (SC-2457): one route per catalog; the section
                  tabs are nested routes so they deep-link + survive back/forward.
                  The segments are the generic, stable canvas view ids (SC-2670) —
                  the display label comes from the catalog's entity term. Unknown
                  sub-paths (e.g. the retired /findings, SC-2427) match no child, so
                  the layout never mounts and they fall through to the catch-all 404
                  below. */}
              <Route path="catalogs/:id" element={<CatalogLayout />}>
                <Route index element={<Navigate to="entities" replace />} />
                <Route path="entities" element={<TableTab />} />
                <Route path="attributes" element={<AttributesRoute />} />
                <Route path="findings" element={<FindingsRoute />} />
                <Route path="profile" element={<ProfileRoute />} />
                <Route path="history" element={<ChangesRoute />} />
                {/* Generative boards (SC-2872) — the URL form of the
                    `board:<boardId>` canvas view id. A bare /boards has no
                    index route on purpose: it 404s like any unknown segment. */}
                <Route path="boards/:boardId" element={<BoardRoute />} />
                {/* Back-compat: the pre-migration segments redirect to their
                    renamed equivalents, preserving query + hash (SC-2670). */}
                <Route path="table" element={<SectionRedirect view="entities" />} />
                <Route path="changes" element={<SectionRedirect view="history" />} />
              </Route>
              <Route path="analytics" element={<AnalyticsTab />} />
              <Route path="connections" element={<ConnectionsTab />} />
              <Route path="connections/:direction/:id" element={<ConnectionDetail />} />
              <Route path="team" element={<TeamTab />} />
              <Route path="settings" element={<SettingsTab />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
        </div>
        <AnalysisStatusBar />
      </div>
    </Sentry.ErrorBoundary>
  );
}

export default App;
